import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, ButtonGroup, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useAppContext } from '../context/AppContext';
import { useEnergyContext } from '../context/EnergyContext';
import { energyService } from '../api/energyApi';
import { energyWindow } from '../utils/energyRanges';
import { matchesCanopyId, normalizeTelemetryRecord } from '../utils/telemetryRecordNormalizer';
import BrowserCanopyList from './BrowserCanopyList';
import Datalens from './Datalens';

// Uses only authenticated, account-scoped energy reads. There is no archive
// import or modification path in this guest browser.
export default function ScopedTelemetryBrowser() {
  const { customerContext, fleetContext } = useAppContext();
  const energy = useEnergyContext();
  const timezone = energy?.timezone || customerContext?.billingTimezone || 'America/New_York';
  const fleetId = fleetContext?.fleetId;
  const [canopyId, setCanopyId] = useState(null);
  const [source, setSource] = useState('latest');
  const [range, setRange] = useState('day');
  const [page, setPage] = useState(0);
  const cursors = useRef([undefined]);
  const [nextCursor, setNextCursor] = useState(null);
  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const [latestRecordTime, setLatestRecordTime] = useState(null);
  const referenceTimeMs = useMemo(() => {
    const candidates = [
      energy?.model?.meta?.balanceCutoffTime,
      energy?.model?.meta?.sourceTime,
      latestRecordTime,
    ]
      .map((value) => (value ? Date.parse(value) : NaN))
      .filter(Number.isFinite);
    return candidates.length ? candidates[0] : Date.now();
  }, [energy?.model?.meta?.balanceCutoffTime, energy?.model?.meta?.sourceTime, latestRecordTime]);
  const windowRange = useMemo(() => ({ ...energyWindow(range, referenceTimeMs, timezone), fleetId, canopyId, source }),
    [range, referenceTimeMs, timezone, fleetId, canopyId, source]);
  const historyStart = source === 'history' ? windowRange.start : null;
  const historyEnd = source === 'history' ? windowRange.end : null;

  useEffect(() => {
    cursors.current = [undefined];
    setPage(0);
    setRecords([]);
    setNextCursor(null);
    setSelectedRecord(null);
  }, [fleetId, canopyId, source, range, timezone]);

  useEffect(() => {
    if (!fleetId || !canopyId) return undefined;
    let cancelled = false;
    const fetchRecords = async () => {
      setLoading(true);
      setError(null);
      try {
        const scope = { fleetId, canopyId, messageType: 'canopy.telemetry' };
        const response = source === 'latest'
          ? await energyService.getLatest(scope)
          : await energyService.getHistory({ ...scope, start: historyStart,
            end: historyEnd, timezone, limit: 100, cursor: cursors.current[page] });
        if (cancelled) return;
        if (!response?.success) throw new Error(response?.message || 'Telemetry could not be loaded.');
        const envelope = response.data;
        const list = Array.isArray(envelope?.data) ? envelope.data : [];
        const normalized = list.filter((record) => matchesCanopyId(record, canopyId)).map((record) =>
          normalizeTelemetryRecord(record, { fleetId, canopyId })).filter(Boolean);
        setRecords(normalized);
        if (source === 'latest' && normalized.length > 0) {
          const newest = normalized.map((r) => Date.parse(r.timestamp || r.observedAt)).filter(Number.isFinite);
          if (newest.length > 0) setLatestRecordTime(new Date(Math.max(...newest)).toISOString());
        }
        const next = envelope?.metadata?.nextCursor || null;
        cursors.current[page + 1] = next;
        setNextCursor(next);
      } catch (err) {
        if (!cancelled) { setRecords([]); setNextCursor(null); setError(err.message); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchRecords();
    const timer = source === 'latest' ? window.setInterval(fetchRecords, 10000) : null;
    return () => { cancelled = true; if (timer) window.clearInterval(timer); };
  }, [fleetId, canopyId, source, page, timezone, historyStart, historyEnd, reload]);

  const formatTime = (value) => value ? new Date(value).toLocaleString('en-US', { timeZone: timezone }) : '-';
  if (!fleetId) return <Alert severity="info">Select a fleet to browse telemetry.</Alert>;
  return <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant="h5">Telemetry Browser</Typography>
      <Typography variant="body2" color="text.secondary">{fleetContext.fleetName || fleetId} · {timezone}</Typography>
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
        <ButtonGroup size="small">
          <Button variant={source === 'latest' ? 'contained' : 'outlined'} onClick={() => setSource('latest')}>Current data</Button>
          <Button variant={source === 'history' ? 'contained' : 'outlined'} onClick={() => setSource('history')}>History</Button>
        </ButtonGroup>
        {source === 'history' && <ButtonGroup size="small" aria-label="Telemetry history range">{['hour', 'day', 'week', 'month'].map((option) =>
          <Button key={option} variant={range === option ? 'contained' : 'outlined'} onClick={() => setRange(option)}>{option}</Button>)}</ButtonGroup>}
        <Button size="small" onClick={() => setReload((value) => value + 1)}>Refresh</Button>
      </Stack>
      {source === 'history' && <Typography variant="caption">{formatTime(windowRange.start)} – {formatTime(windowRange.end)}</Typography>}
    </Box>
    <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
      <Box sx={{ flex: 1, p: 2, minWidth: 0, overflow: 'auto' }}>
        {!canopyId ? <Typography>Select a canopy to browse telemetry.</Typography> : <>
          <Typography variant="h6">{canopyId}</Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {loading ? <CircularProgress size={24} aria-label="Loading telemetry" /> : !records.length ? <Typography>No records in this period.</Typography> :
            <TableContainer component={Paper}><Table size="small" aria-label="Telemetry records"><TableHead><TableRow>
              {['Timestamp', 'Device', 'Sequence', 'State', 'Tilt angle'].map((label) => <TableCell key={label}>{label}</TableCell>)}
            </TableRow></TableHead><TableBody>{records.map((record) => {
              const controller = record.data?.sabalController;
              return <TableRow key={record.messageId || record.id} hover onClick={() => setSelectedRecord(record)} sx={{ cursor: 'pointer' }}>
                <TableCell>{formatTime(record.timestamp)}</TableCell><TableCell>{record.deviceId}</TableCell>
                <TableCell>{record.sequenceNumber ?? '-'}</TableCell><TableCell>{controller?.operating?.mode || controller?.systemInfo?.canopyStateText || '-'}</TableCell>
                <TableCell>{controller?.operating?.actualTiltDeg === null || controller?.operating?.actualTiltDeg === undefined ? '-' : `${controller.operating.actualTiltDeg}°`}</TableCell>
              </TableRow>;
            })}</TableBody></Table></TableContainer>}
          {source === 'history' && <Stack direction="row" spacing={2} sx={{ mt: 2 }} alignItems="center">
            <Button disabled={!page || loading} onClick={() => setPage((value) => value - 1)}>Previous</Button>
            <Typography variant="caption">Page {page + 1} · {records.length} records</Typography>
            <Button disabled={!nextCursor || loading} onClick={() => setPage((value) => value + 1)}>Next</Button>
          </Stack>}
        </>}
      </Box>
      <Box sx={{ width: 300, borderLeft: 1, borderColor: 'divider' }}><BrowserCanopyList onCanopySelection={(ids) => setCanopyId(ids[0] || null)} /></Box>
    </Box>
    <Datalens record={selectedRecord} open={Boolean(selectedRecord)} onClose={() => setSelectedRecord(null)} modal />
  </Box>;
}
